import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO31000_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 31000 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Risk Management — International standard for structured, proactive
            risk identification, assessment, and mitigation to support
            organizational objectives.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 31000 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 31000:2018 is the international standard for Risk Management,
              providing a structured framework and process for identifying,
              assessing, managing, and mitigating risks in order to achieve
              business objectives, enhance decision-making, and improve
              organizational resilience. The standard offers a comprehensive and
              universally applicable approach to managing uncertainty and
              potential threats across all areas of an organization.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO 31000 certification demonstrates an organization's
              commitment to effective risk management practices, ensuring that
              risks are consistently identified, evaluated, and managed to
              minimize negative impacts and seize opportunities for growth.
              Whether dealing with financial risks, operational disruptions,
              legal challenges, or strategic uncertainties, ISO 31000 helps
              businesses build a resilient framework to manage risks and enhance
              their ability to achieve long-term success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 31000 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 31000 certification provides a globally recognized framework
              for risk management that can be applied to any organization,
              regardless of size, industry, or sector. The standard outlines the
              principles, framework, and processes required to manage risk
              effectively, ensuring that organizations can identify and address
              risks proactively rather than reactively.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO 31000 helps organizations:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Identify and assess potential risks across all functions.",
                "Implement risk treatment strategies that align with business objectives.",
                "Create a culture of risk management that is integrated into decision-making processes.",
                "Improve overall organizational resilience by reducing the likelihood and impact of disruptions.",
                "Enhance stakeholder confidence through consistent, transparent, and well-documented risk management processes.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 31000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Risk Management Principles",
                desc: "ISO 31000 defines a set of core principles for effective risk management, including the need for a structured and transparent process, the integration of risk management into organizational governance, and a continual improvement approach to managing risks.",
              },
              {
                icon: Globe,
                title: "Risk Management Framework",
                desc: "The standard provides a comprehensive framework that aligns risk management with an organization’s strategy, governance, and objectives. The framework emphasizes the importance of leadership commitment, stakeholder engagement, and the integration of risk management into the organization’s overall management system.",
              },
              {
                icon: Target,
                title: "Risk Management Process",
                desc: "ISO 31000 outlines a clear and structured risk management process that includes risk identification, risk assessment, risk treatment, monitoring and review, and communication and consultation.",
              },
              {
                icon: Users,
                title: "Integration into Organizational Culture",
                desc: "ISO 31000 emphasizes that risk management should be integrated into the organization’s culture and decision-making processes, rather than being treated as a standalone activity. This ensures that risk management is continuously considered in everyday operations and strategic planning.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO 31000 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Proactive Risk Identification and Management — ISO 31000 ensures that organizations can identify and assess risks before they materialize, enabling proactive management strategies to mitigate potential threats and seize opportunities.",
                "Improved Decision-Making — By integrating risk management into the decision-making process, organizations can make more informed decisions based on a comprehensive understanding of the risks and opportunities they face.",
                "Enhanced Organizational Resilience — The standard helps organizations build resilience by ensuring that risks are managed effectively, reducing the likelihood and impact of disruptions. This allows businesses to continue operating even in the face of challenges or adverse events.",
                "Better Resource Allocation — ISO 31000 helps organizations allocate resources more effectively by focusing on risks that have the greatest potential impact. It ensures that risk management efforts are aligned with business objectives, helping organizations achieve their goals more efficiently.",
                "Compliance with Legal and Regulatory Requirements — Achieving ISO 31000 certification helps businesses meet legal, regulatory, and contractual obligations related to risk management. The standard provides a structured approach to risk that ensures organizations are compliant with industry regulations.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO 31000 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 31000 provides a comprehensive approach to risk management,
              covering the following key components:
            </p>
            <div className="space-y-4">
              {[
                "1. Leadership and Commitment — Senior management must demonstrate commitment to risk management by providing leadership, ensuring adequate resources, and embedding risk management into the organization's governance and decision-making structures.",
                "2. Risk Management Policy — Develop a risk management policy that outlines the organization’s approach to managing risks, including objectives, roles, and responsibilities for risk management across all levels of the organization.",
                "3. Risk Identification and Assessment — Identify and assess potential risks that could impact the achievement of business objectives. This includes evaluating the likelihood, consequences, and impact of each identified risk.",
                "4. Risk Treatment and Mitigation — Develop risk treatment strategies to mitigate the impact of identified risks. This includes implementing controls, processes, and procedures that reduce the likelihood of risks or minimize their impact on operations.",
                "5. Monitoring and Review — Continuously monitor and review the effectiveness of risk management strategies and controls to ensure that they remain effective in mitigating risks and supporting business goals.",
                "6. Communication and Consultation — Ensure that risk management is communicated clearly across the organization and that stakeholders are consulted regularly to ensure that risks are managed transparently and effectively.",
                "7. Continuous Improvement — ISO 31000 promotes a continuous improvement approach to risk management, ensuring that risk management practices evolve and adapt to changing risks, business conditions, and stakeholder expectations.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO 31000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved Risk Management",
                "Enhanced Decision-Making",
                "Organizational Resilience",
                "Compliance and Risk Mitigation",
                "Better Resource Management",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO 31000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The process of obtaining ISO 31000 certification typically
              involves the following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Establish a Risk Management Framework — Develop a structured risk management framework that aligns with your organization’s objectives and integrates with the broader governance and strategic planning processes.",
                "2. Conduct a Risk Assessment — Identify and assess risks across all functions and levels of the organization, evaluating their potential impact on business objectives.",
                "3. Implement Risk Treatment Plans — Develop and implement risk treatment plans to manage, mitigate, or exploit identified risks. This may include the application of controls, procedures, or contingency plans.",
                "4. Establish a Risk Management Policy — Create a risk management policy that outlines the organization's approach to managing risks, defining roles, responsibilities, and processes.",
                "5. Monitor and Review Performance — Regularly monitor and review the effectiveness of risk management strategies, ensuring that they are delivering the intended results and adapting to changes in the risk landscape.",
                "6. Engage Stakeholders — Engage key stakeholders, including employees, customers, suppliers, and regulators, to ensure that risk management is aligned with organizational objectives and stakeholder expectations.",
                "7. Third-Party Certification Audit — Work with an accredited certification body to conduct a third-party audit to verify that your risk management practices align with ISO 31000 standards.",
                "8. Continuous Improvement — Establish processes for continuous improvement to ensure that risk management practices evolve in response to emerging risks, changing business needs, and external conditions.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 31000 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert ISO 31000 certification
              consultancy services, helping organizations implement an effective
              risk management system that aligns with business objectives,
              enhances decision-making, and improves organizational resilience.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Risk Management Strategy Development",
                "Risk Identification and Assessment",
                "Risk Treatment and Mitigation Plans",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO 31000 certification, ensuring
              effective risk management, improved decision-making, and
              organizational resilience.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO31000_Details;
